// Enhanced AI Service with complete response capture
import { getAPIHeaders } from '../utils/apiHeaders';

// Use empty string for Vite proxy in dev, full URL in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
export const AI_API_URL = `${API_BASE_URL}/api/ai`;

export interface AIProviderDetails {
  provider: string;
  request: {
    url: string;
    method: string;
    headers: Record<string, string>;
    body: any;
    timestamp: string;
  };
  response: {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    data: any;
    timestamp: string;
  };
  responseTime: number;
}

export interface EnhancedResponse {
  html: string;
  title: string;
  description: string;
  responseHeaders: Record<string, string>;
  status: number;
  statusText: string;
  rawResponse?: any;
  aiProviderDetails?: AIProviderDetails;
  usage?: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
  };
  metadata?: any;
}

// Enhanced AI API service with complete response capture
export class AIServiceEnhanced {
  constructor() {
    // No need to store API key in frontend anymore
  }

  async generateLensHTMLWithDetails(
    code: string,
    knownLanguage: string,
    targetLanguage: string,
    concept?: string,
    provider?: any,
    promptVersion?: string,
    options?: { enableLanguagePairPrompts?: boolean; enablePromptCaching?: boolean }
  ): Promise<EnhancedResponse> {
    try {
      const requestBody = {
        type: 'lens-html',
        code,
        sourceLanguage: knownLanguage,
        targetLanguage,
        concept,
        provider,
        promptVersion: promptVersion || 'v1', // Default to v1 if not specified
        returnFullDetails: true, // Request full AI provider details for debug
        // Pass admin settings to backend (if provided)
        enableLanguagePairPrompts: options?.enableLanguagePairPrompts,
        enablePromptCaching: options?.enablePromptCaching
      };

      // DEBUG: Log request body with language-pair settings
      console.log('📤 [aiServiceEnhanced] Request body:', {
        sourceLanguage: knownLanguage,
        targetLanguage,
        provider: typeof provider === 'object' ? provider.provider : provider,
        enableLanguagePairPrompts: requestBody.enableLanguagePairPrompts,
        enablePromptCaching: requestBody.enablePromptCaching,
        promptVersion: requestBody.promptVersion
      });

      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify(requestBody)
      });

      // Capture all response headers
      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Backend API Error: ${response.status} - ${errorText}`);
      }

      // Check if response is JSON (with full details) or plain HTML
      const contentType = response.headers.get('content-type');
      let html: string;
      let aiProviderDetails: AIProviderDetails | undefined;
      let usage: any;
      let metadata: any;
      let parsedData: any = null;
      
      if (contentType?.includes('application/json')) {
        // We got full details including AI provider info
        parsedData = await response.json();
        html = parsedData.html;
        aiProviderDetails = parsedData.aiProviderDetails;
        usage = parsedData.usage;
        metadata = parsedData.metadata;
        
        console.log('📊 Received AI provider details:', {
          provider: aiProviderDetails?.provider,
          requestTime: aiProviderDetails?.request?.timestamp,
          responseTime: aiProviderDetails?.response?.timestamp,
          model: metadata?.selectedModel,
          tokens: usage
        });
      } else {
        // Backward compatibility: just HTML
        html = await response.text();
      }

      return {
        html,
        title: `${knownLanguage} to ${targetLanguage}${concept ? `: ${concept}` : ''}`,
        description: `Interactive code with lens insights`,
        responseHeaders,
        status: response.status,
        statusText: response.statusText,
        rawResponse: parsedData || { html },
        aiProviderDetails,
        usage,
        metadata
      };
    } catch (error) {
      console.error('Error calling AI API for lens HTML:', error);
      throw error;
    }
  }

  async *generateLensHTMLStream(
    code: string,
    knownLanguage: string,
    targetLanguage: string,
    concept?: string,
    provider?: any,
    promptVersion?: string,
    options?: { enableLanguagePairPrompts?: boolean; enablePromptCaching?: boolean }
  ): AsyncGenerator<{ type: string; content?: string; totalContent?: string; error?: string; usage?: any; metadata?: any; aiProviderDetails?: AIProviderDetails }> {

    const requestBody = {
      type: 'lens-html',
      code,
      sourceLanguage: knownLanguage,
      targetLanguage,
      concept,
      provider,
      promptVersion: promptVersion || 'v1',
      stream: true,  // Enable streaming
      // Pass admin settings to backend (if provided)
      enableLanguagePairPrompts: options?.enableLanguagePairPrompts,
      enablePromptCaching: options?.enablePromptCaching
    };

    console.log(`🌊 Streaming: ${knownLanguage} → ${targetLanguage} (${code.length} chars)`);

    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    let abortController: AbortController | null = null;

    try {
      // Create abort controller to prevent premature cancellation
      abortController = new AbortController();

      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify(requestBody),
        signal: abortController.signal,
        // Important: Keep connection alive
        keepalive: false
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not OK:', response.status, errorText);
        throw new Error(`Backend API Error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null - streaming not supported');
      }

      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventCount = 0;

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();

            if (!data) continue;

            try {
              const event = JSON.parse(data);
              eventCount++;

              if (event.type === 'chunk') {
                yield {
                  type: 'chunk',
                  content: event.content,
                  totalContent: event.totalContent
                };
              } else if (event.type === 'done') {
                yield {
                  type: 'done',
                  usage: event.usage,
                  metadata: event.metadata,
                  aiProviderDetails: event.aiProviderDetails // Pass through AI provider details for debug
                };
              } else if (event.type === 'error') {
                console.error('❌ Server sent error event:', event.message);
                yield {
                  type: 'error',
                  error: event.message
                };
                return;
              } else if (event.type === 'complete') {
                console.log(`✅ Stream complete (${eventCount} events)`);
                yield { type: 'complete' };
                return;
              }
            } catch (e) {
              console.warn('⚠️ Failed to parse SSE event:', data.substring(0, 100), e);
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ Streaming error:', error);
      yield {
        type: 'error',
        error: error instanceof Error ? error.message : 'Unknown streaming error'
      };
      throw error;
    } finally {
      // Clean up reader
      if (reader) {
        try {
          reader.releaseLock();
        } catch (e) {
          console.warn('⚠️ Failed to release reader:', e);
        }
      }
    }
  }

  async generateDiffAnalysis(
    code: string,
    sourceLanguage: string,
    targetLanguage: string,
    provider?: any
  ): Promise<EnhancedResponse> {
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...getAPIHeaders()
      };

      const body: any = {
        type: 'diff-analysis',
        code,
        sourceLanguage,
        targetLanguage,
      };

      if (provider) {
        body.provider = provider;
      }

      const response = await fetch(AI_API_URL, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API request failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const responseHeaders: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      let html = '';
      let aiProviderDetails: AIProviderDetails | undefined;
      let usage: any;
      let metadata: any;

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const parsedData = await response.json();
        html = parsedData.html || parsedData.content || '';
        aiProviderDetails = parsedData.aiProviderDetails;
        usage = parsedData.usage;
        metadata = parsedData.metadata;
      } else {
        html = await response.text();
      }

      return {
        html,
        title: `Diff Analysis: ${sourceLanguage} vs ${targetLanguage}`,
        description: 'Syntax and semantic comparison',
        responseHeaders,
        status: response.status,
        statusText: response.statusText,
        rawResponse: { html },
        aiProviderDetails,
        usage,
        metadata
      };
    } catch (error) {
      console.error('Error calling AI API for diff analysis:', error);
      throw error;
    }
  }
}

export default AIServiceEnhanced;